import { CurrencyCard } from '../../components/admin/settings/CurrencyCard';
import { cs } from '../../lib/currency';
import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import TenantDriftPanel from '../super-admin/TenantDriftPanel';
import PaymentMethodsDirectory from '../../components/admin/PaymentMethodsDirectory';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  CreditCard,
  Save,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Shield,
  TestTube,
  Rocket,
  Info,
  Loader2,
  ExternalLink,
  Activity,
  RefreshCw,
  Clock,
  Webhook,
  Mail,
  Send,
  Trash2,
  DollarSign,
  Users,
  Zap,
} from 'lucide-react';

const StripeSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  
  // Webhook activity states
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [paymentSyncStatus, setPaymentSyncStatus] = useState(null);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [eventTypes, setEventTypes] = useState([]);
  
  // Invoice reminder states
  const [reminderStats, setReminderStats] = useState(null);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);
  
  // Form states
  const [webhookUrls, setWebhookUrls] = useState({ test: null, live: null });
  const [webhookUrlsLoading, setWebhookUrlsLoading] = useState(false);
  const [testKeys, setTestKeys] = useState({
    secret_key: '',
    publishable_key: '',
    webhook_secret: ''
  });
  
  const [liveKeys, setLiveKeys] = useState({
    secret_key: '',
    publishable_key: '',
    webhook_secret: ''
  });
  
  // UI states
  const [showTestSecret, setShowTestSecret] = useState(false);
  const [showLiveSecret, setShowLiveSecret] = useState(false);
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [showLiveKeysDialog, setShowLiveKeysDialog] = useState(false);
  const [confirmLiveKeys, setConfirmLiveKeys] = useState(false);
  const [confirmModeSwitch, setConfirmModeSwitch] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  
  // CC Surcharge states
  const [ccSurchargeEnabled, setCcSurchargeEnabled] = useState(true);
  const [ccSurchargePercent, setCcSurchargePercent] = useState(3.0);
  const [savingSurcharge, setSavingSurcharge] = useState(false);

  // iter334c — Auto-charge master switch (tenant-level).
  // Off = cron generates invoices but never charges any card.
  // On  = cron auto-charges parents with `auto_pay_acknowledged` + saved Stripe customer.
  const [autoChargeEnabled, setAutoChargeEnabled] = useState(false);
  const [savingAutoCharge, setSavingAutoCharge] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchWebhookActivity();
    fetchReminderStats();
    fetchSurchargeSettings();
    fetchTenantWebhookUrls();
  }, []);

  // ARM-audit PR A / CR-02A (2026-07-17) — load the opaque tenant
  // webhook URL from the backend so Stripe Settings never surfaces the
  // shared legacy `/api/webhook/stripe` URL. Rotate is available via
  // POST /admin/billing/stripe-webhook-url/rotate.
  const fetchTenantWebhookUrls = async () => {
    setWebhookUrlsLoading(true);
    try {
      const [test, live] = await Promise.all([
        api.get('/admin/billing/stripe-webhook-url?mode=test').catch(() => null),
        api.get('/admin/billing/stripe-webhook-url?mode=live').catch(() => null),
      ]);
      setWebhookUrls({
        test: test?.data?.webhook_url || null,
        live: live?.data?.webhook_url || null,
      });
    } finally {
      setWebhookUrlsLoading(false);
    }
  };

  const rotateTenantWebhookUrl = async (mode) => {
    if (!window.confirm(
      `Rotate the ${mode} webhook URL? The previous URL will stop accepting Stripe deliveries immediately. ` +
      `Update Stripe Dashboard → Developers → Webhooks with the new URL right after.`
    )) return;
    try {
      const r = await api.post(`/admin/billing/stripe-webhook-url/rotate?mode=${mode}`);
      setWebhookUrls((prev) => ({ ...prev, [mode]: r.data.webhook_url }));
      toast.success(`Rotated ${mode} webhook URL — update Stripe Dashboard now`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rotation failed');
    }
  };

  const fetchSettings = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        api.get('/admin/stripe-settings'),
        api.get('/admin/stripe-settings/status')
      ]);
      setSettings(settingsRes.data);
      setStatus(statusRes.data);
    } catch (error) {
      toast.error('Failed to load Stripe settings');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchWebhookActivity = async () => {
    setLoadingWebhooks(true);
    try {
      const [logsRes, syncRes] = await Promise.all([
        api.get('/admin/webhook-logs?limit=50'),
        RETIRED_TOOLS_VISIBLE ? api.get('/admin/payment-sync-status') : Promise.resolve({ data: null }),
      ]);
      setWebhookLogs(logsRes.data.logs || []);
      setEventTypes(logsRes.data.event_types || []);
      setPaymentSyncStatus(syncRes.data);
    } catch (error) {
      console.error('Failed to load webhook activity:', error);
    } finally {
      setLoadingWebhooks(false);
    }
  };
  
  const fetchReminderStats = async () => {
    setLoadingReminders(true);
    try {
      const res = await api.get('/admin/jobs/invoice-reminders/stats');
      setReminderStats(res.data);
    } catch (error) {
      console.error('Failed to load reminder stats:', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const fetchSurchargeSettings = async () => {
    try {
      const res = await api.get('/admin/billing-settings');
      if (res.data) {
        setCcSurchargeEnabled(res.data.cc_surcharge_enabled ?? true);
        setCcSurchargePercent(res.data.cc_surcharge_percent ?? 3.0);
        // iter334c — hydrate auto-charge master switch off the same
        // /admin/billing-settings row (single source of truth).
        setAutoChargeEnabled(res.data.auto_charge_enabled ?? false);
      }
    } catch (error) {
      console.error('Failed to load surcharge settings:', error);
    }
  };

  // iter334c — Persist auto-charge master switch.
  const saveAutoChargeSetting = async (nextValue) => {
    setSavingAutoCharge(true);
    try {
      await api.put('/admin/billing-settings', { auto_charge_enabled: nextValue });
      setAutoChargeEnabled(nextValue);
      toast.success(
        nextValue
          ? 'Auto-charge ENABLED — enrolled parents with saved payment methods will be charged automatically on the next cron run.'
          : 'Auto-charge DISABLED — the cron will still generate invoices, but no card will be charged.',
      );
    } catch (error) {
      toast.error('Failed to save auto-charge setting');
    } finally {
      setSavingAutoCharge(false);
    }
  };

  const saveSurchargeSettings = async () => {
    setSavingSurcharge(true);
    try {
      await api.put('/admin/billing-settings', {
        cc_surcharge_enabled: ccSurchargeEnabled,
        cc_surcharge_percent: parseFloat(ccSurchargePercent) || 0
      });
      toast.success('CC processing fee settings saved');
    } catch (error) {
      toast.error('Failed to save surcharge settings');
    } finally {
      setSavingSurcharge(false);
    }
  };

  
  const runInvoiceReminders = async () => {
    setRunningReminders(true);
    try {
      const res = await api.post('/admin/jobs/invoice-reminders/run');
      const result = res.data.result;
      if (result.sent > 0) {
        toast.success(`Sent ${result.sent} reminder email(s)`);
      } else if (result.processed === 0) {
        toast.info('No overdue invoices to remind');
      } else {
        toast.warning(`Processed ${result.processed}, but ${result.failed} failed`);
      }
      fetchReminderStats();
    } catch (error) {
      toast.error('Failed to run invoice reminders');
    } finally {
      setRunningReminders(false);
    }
  };
  
  const resetReminderTracking = async () => {
    if (!window.confirm('This will reset all reminder tracking, allowing immediate re-sending of reminders. Continue?')) {
      return;
    }
    try {
      await api.delete('/admin/jobs/invoice-reminders/reset');
      toast.success('Reminder tracking reset');
      fetchReminderStats();
    } catch (error) {
      toast.error('Failed to reset tracking');
    }
  };
  
  const refreshWebhookLogs = async () => {
    setLoadingWebhooks(true);
    try {
      const filter = selectedEventType !== 'all' ? `&event_type=${selectedEventType}` : '';
      const response = await api.get(`/admin/webhook-logs?limit=50${filter}`);
      setWebhookLogs(response.data.logs || []);
      setEventTypes(response.data.event_types || []);
      toast.success('Webhook logs refreshed');
    } catch (error) {
      toast.error('Failed to refresh webhook logs');
    } finally {
      setLoadingWebhooks(false);
    }
  };

  const handleSaveTestKeys = async () => {
    if (!testKeys.secret_key && !testKeys.publishable_key && !testKeys.webhook_secret) {
      toast.error('Please enter at least one key to update');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.put('/admin/stripe-settings/test-keys', testKeys);
      toast.success(response.data.message);
      if (response.data.warning) {
        toast.info(response.data.warning);
      }
      setTestKeys({ secret_key: '', publishable_key: '', webhook_secret: '' });
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save test keys');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLiveKeys = async () => {
    if (!liveKeys.secret_key && !liveKeys.publishable_key && !liveKeys.webhook_secret) {
      toast.error('Please enter at least one key to update');
      return;
    }
    
    if (!confirmLiveKeys) {
      setShowLiveKeysDialog(true);
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.put('/admin/stripe-settings/live-keys', {
        ...liveKeys,
        confirm_live: true
      });
      toast.success(response.data.message);
      if (response.data.warning) {
        toast.warning(response.data.warning, { duration: 8000 });
      }
      setLiveKeys({ secret_key: '', publishable_key: '', webhook_secret: '' });
      setConfirmLiveKeys(false);
      setShowLiveKeysDialog(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save live keys');
    } finally {
      setSaving(false);
    }
  };

  const handleModeSwitch = async () => {
    if (!confirmModeSwitch) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.put('/admin/stripe-settings/mode', {
        mode: pendingMode,
        confirm_switch: true
      });
      
      if (pendingMode === 'live') {
        toast.warning(response.data.warning, { duration: 10000 });
      } else {
        toast.success(response.data.message);
      }
      
      setShowModeDialog(false);
      setConfirmModeSwitch(false);
      setPendingMode(null);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch mode');
    } finally {
      setSaving(false);
    }
  };

  const initiateModeSwitchToLive = () => {
    if (!settings?.live_keys?.is_configured) {
      toast.error('Please configure your live API keys before switching to live mode');
      return;
    }
    setPendingMode('live');
    setShowModeDialog(true);
  };

  const initiateModeSwichToTest = () => {
    setPendingMode('test');
    setShowModeDialog(true);
  };

  if (loading) {
    return (
      <DashboardLayout title="Payment Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const isLiveMode = settings?.mode === 'live';

  return (
    <DashboardLayout title="Payment Settings">
      <div className="space-y-6" data-testid="stripe-settings-page">
        
        {/* Status Banner */}
        {isLiveMode ? (
          <Alert className="border-red-300 bg-red-50">
            <Rocket className="h-5 w-5 text-red-600" />
            <AlertTitle className="text-red-800 font-bold">LIVE MODE ACTIVE</AlertTitle>
            <AlertDescription className="text-red-700">
              Real payments are being processed. Customers will be charged actual money.
              Switch to Test Mode for development and testing.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-blue-300 bg-blue-50">
            <TestTube className="h-5 w-5 text-blue-600" />
            <AlertTitle className="text-blue-800 font-semibold">Test Mode Active</AlertTitle>
            <AlertDescription className="text-blue-700">
              Using test API keys. No real payments will be processed.
              Perfect for testing your payment flows.
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs for Settings and Webhook Activity */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-5">
            <TabsTrigger value="settings" data-testid="settings-tab">
              <CreditCard className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="saved-pms" data-testid="saved-pms-tab">
              <CreditCard className="h-4 w-4 mr-2" />
              Saved PMs
            </TabsTrigger>
            <TabsTrigger value="webhooks" data-testid="webhooks-tab">
              <Activity className="h-4 w-4 mr-2" />
              Webhooks
            </TabsTrigger>
            <TabsTrigger value="reminders" data-testid="reminders-tab">
              <Clock className="h-4 w-4 mr-2" />
              Invoice Reminders
            </TabsTrigger>
            <TabsTrigger value="drift" data-testid="drift-tab">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Stripe Sync Findings
            </TabsTrigger>
          </TabsList>
          
          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6 space-y-6">
            <CurrencyCard />
            {/* Current Status Card */}
            <Card className="border border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Stripe Integration Status
                </CardTitle>
                <CardDescription>Current payment processing configuration</CardDescription>
              </div>
              <Badge 
                className={`text-sm px-3 py-1 ${
                  status?.is_operational 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {status?.is_operational ? (
                  <><CheckCircle className="h-4 w-4 mr-1" /> Operational</>
                ) : (
                  <><XCircle className="h-4 w-4 mr-1" /> Not Configured</>
                )}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500 mb-1">Current Mode</p>
                <div className="flex items-center gap-2">
                  {isLiveMode ? (
                    <Badge className="bg-red-100 text-red-700">
                      <Rocket className="h-3 w-3 mr-1" /> LIVE
                    </Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700">
                      <TestTube className="h-3 w-3 mr-1" /> TEST
                    </Badge>
                  )}
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500 mb-1">Test Keys</p>
                <div className="flex items-center gap-2">
                  {settings?.test_keys?.is_configured ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">
                      <AlertTriangle className="h-3 w-3 mr-1" /> Not Set
                    </Badge>
                  )}
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500 mb-1">Live Keys</p>
                <div className="flex items-center gap-2">
                  {settings?.live_keys?.is_configured ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600">
                      <XCircle className="h-3 w-3 mr-1" /> Not Set
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Mode Switch Buttons */}
            <div className="mt-6 flex items-center gap-4">
              <Button
                variant={!isLiveMode ? "default" : "outline"}
                onClick={initiateModeSwichToTest}
                disabled={!isLiveMode || saving}
                className={!isLiveMode ? "bg-blue-600" : ""}
                data-testid="switch-to-test-btn"
              >
                <TestTube className="h-4 w-4 mr-2" />
                Test Mode
              </Button>
              <Button
                variant={isLiveMode ? "default" : "outline"}
                onClick={initiateModeSwitchToLive}
                disabled={isLiveMode || saving || !settings?.live_keys?.is_configured}
                className={isLiveMode ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="switch-to-live-btn"
              >
                <Rocket className="h-4 w-4 mr-2" />
                Live Mode
              </Button>
              {!settings?.live_keys?.is_configured && (
                <span className="text-sm text-slate-500">
                  (Configure live keys first to enable live mode)
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Test Keys Card */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <TestTube className="h-5 w-5 text-blue-600" />
                  Test API Keys
                </CardTitle>
                <CardDescription>
                  Use these keys for development and testing. No real charges.
                </CardDescription>
              </div>
              <a 
                href="https://dashboard.stripe.com/test/apikeys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Get Test Keys <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </CardHeader>
          <CardContent className="p-4 lg:p-6 space-y-4">
            {settings?.test_keys?.is_configured && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  Test keys are configured. Publishable key: {settings.test_keys.publishable_key}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label>Secret Key (sk_test_...)</Label>
              <div className="relative">
                <Input
                  type={showTestSecret ? "text" : "password"}
                  value={testKeys.secret_key}
                  onChange={(e) => setTestKeys({...testKeys, secret_key: e.target.value})}
                  placeholder="sk_test_..."
                  className="pr-10"
                  data-testid="test-secret-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowTestSecret(!showTestSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showTestSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Publishable Key (pk_test_...)</Label>
              <Input
                value={testKeys.publishable_key}
                onChange={(e) => setTestKeys({...testKeys, publishable_key: e.target.value})}
                placeholder="pk_test_..."
                data-testid="test-publishable-key-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Webhook Secret (whsec_...) - Optional</Label>
              <Input
                type="password"
                value={testKeys.webhook_secret}
                onChange={(e) => setTestKeys({...testKeys, webhook_secret: e.target.value})}
                placeholder="whsec_..."
                data-testid="test-webhook-secret-input"
              />
              <p className="text-xs text-slate-500">
                Required for webhook event verification. Get it from Stripe Dashboard → Webhooks.
              </p>
            </div>
            
            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSaveTestKeys}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-test-keys-btn"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Test Keys</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Live Keys Card */}
        <Card className="border-2 border-red-200">
          <CardHeader className="bg-red-50 border-b border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-red-600" />
                  Live API Keys
                </CardTitle>
                <CardDescription className="text-red-700">
                  ⚠️ These keys process REAL payments. Handle with care!
                </CardDescription>
              </div>
              <a 
                href="https://dashboard.stripe.com/apikeys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                Get Live Keys <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </CardHeader>
          <CardContent className="p-4 lg:p-6 space-y-4">
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Important</AlertTitle>
              <AlertDescription className="text-amber-700">
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Live keys will charge real money to customers</li>
                  <li>Never share your secret key with anyone</li>
                  <li>Test thoroughly with test keys before going live</li>
                  <li>Ensure your Stripe account is fully verified</li>
                </ul>
              </AlertDescription>
            </Alert>

            {settings?.live_keys?.is_configured && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  Live keys are configured. Publishable key: {settings.live_keys.publishable_key}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label>Secret Key (sk_live_...)</Label>
              <div className="relative">
                <Input
                  type={showLiveSecret ? "text" : "password"}
                  value={liveKeys.secret_key}
                  onChange={(e) => setLiveKeys({...liveKeys, secret_key: e.target.value})}
                  placeholder="sk_live_..."
                  className="pr-10"
                  data-testid="live-secret-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowLiveSecret(!showLiveSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showLiveSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Publishable Key (pk_live_...)</Label>
              <Input
                value={liveKeys.publishable_key}
                onChange={(e) => setLiveKeys({...liveKeys, publishable_key: e.target.value})}
                placeholder="pk_live_..."
                data-testid="live-publishable-key-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Webhook Secret (whsec_...) - Optional</Label>
              <Input
                type="password"
                value={liveKeys.webhook_secret}
                onChange={(e) => setLiveKeys({...liveKeys, webhook_secret: e.target.value})}
                placeholder="whsec_..."
                data-testid="live-webhook-secret-input"
              />
            </div>
            
            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setShowLiveKeysDialog(true)}
                disabled={saving || (!liveKeys.secret_key && !liveKeys.publishable_key)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="save-live-keys-btn"
              >
                <Shield className="h-4 w-4 mr-2" /> Save Live Keys
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Instructions Card */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Info className="h-5 w-5 text-slate-600" />
              How to Get Your Stripe Keys
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-3 text-slate-600">
              <li>
                Go to <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">dashboard.stripe.com</a> and sign in (or create an account)
              </li>
              <li>
                Click <strong>Developers</strong> in the left sidebar, then <strong>API Keys</strong>
              </li>
              <li>
                For <strong>Test Mode</strong>: Make sure &quot;Test mode&quot; toggle is ON (orange bar visible)
              </li>
              <li>
                Copy your <strong>Publishable key</strong> (starts with pk_test_ or pk_live_)
              </li>
              <li>
                Click &quot;Reveal live/test key&quot; to copy your <strong>Secret key</strong> (starts with sk_test_ or sk_live_)
              </li>
              <li>
                For webhooks: Go to <strong>Developers → Webhooks</strong>, add endpoint, copy the signing secret
              </li>
            </ol>
            
            <div className="p-4 bg-slate-50 rounded-lg mt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">
                Webhook URL for your academy{" "}
                <span className="text-xs text-emerald-700 font-normal">
                  (tenant-scoped — CR-02)
                </span>:
              </p>
              {webhookUrlsLoading ? (
                <p className="text-xs text-slate-500">Loading…</p>
              ) : (
                <div className="space-y-1.5" data-testid="tenant-webhook-url-block">
                  <code
                    className="text-sm bg-slate-200 px-2 py-1 rounded block break-all"
                    data-testid="tenant-webhook-url-test"
                  >
                    {webhookUrls.test || 'Not provisioned'}
                  </code>
                  {webhookUrls.live && (
                    <code
                      className="text-sm bg-slate-200 px-2 py-1 rounded block break-all"
                      data-testid="tenant-webhook-url-live"
                    >
                      {webhookUrls.live}
                    </code>
                  )}
                  <p className="text-xs text-amber-700">
                    Use the URL for the Stripe mode you are configuring
                    (test key → test URL; live key → live URL). The shared{" "}
                    <code>/api/webhook/stripe</code> URL is deprecated.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

            {/* iter334c — Auto-Charge Master Switch */}
            <Card className="border border-slate-200" data-testid="auto-charge-settings-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg lg:text-xl flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Auto-Charge (Master Switch)
                </CardTitle>
                <CardDescription>
                  Controls whether the monthly-billing cron automatically charges saved payment methods. Each parent must still individually opt in via <em>auto-pay acknowledgement</em>.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 lg:p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="pr-4">
                    <Label className="text-sm font-medium">Enable Auto-Charge for Monthly Billing</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {autoChargeEnabled
                        ? 'Enrolled parents with saved cards / ACH and auto-pay opt-in will be charged automatically when the cron runs.'
                        : 'Invoices are generated & emailed — parents pay manually. No card will be charged automatically.'}
                    </p>
                  </div>
                  <Switch
                    checked={autoChargeEnabled}
                    onCheckedChange={saveAutoChargeSetting}
                    disabled={savingAutoCharge}
                    data-testid="auto-charge-master-switch"
                  />
                </div>
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <strong>Tip:</strong> Even when this switch is ON, an individual parent is only auto-charged when they have (i) <code>auto_pay_acknowledged=true</code> and (ii) a saved Stripe payment method. Use the DevTools preview at <code>/api/admin/billing/monthly-cron-preview</code> before pressing &ldquo;Run Now&rdquo; to see the exact auto-charge vs manual split.
                </div>
              </CardContent>
            </Card>

            {/* CC Processing Fee Settings */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg lg:text-xl flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  CC Processing Fee
                </CardTitle>
                <CardDescription>
                  Add a surcharge to credit card payments. Automatically refunded for ACH/bank transfers.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 lg:p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Enable CC Surcharge</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {ccSurchargeEnabled 
                        ? 'A processing fee is added to credit card checkouts' 
                        : 'No processing fee will be added'}
                    </p>
                  </div>
                  <Switch
                    checked={ccSurchargeEnabled}
                    onCheckedChange={setCcSurchargeEnabled}
                    data-testid="cc-surcharge-toggle"
                  />
                </div>

                {ccSurchargeEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="surcharge-percent">Surcharge Percentage (%)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="surcharge-percent"
                        type="number"
                        step="0.1"
                        min="0"
                        max="25"
                        value={ccSurchargePercent}
                        onChange={(e) => setCcSurchargePercent(e.target.value)}
                        className="w-28"
                        data-testid="cc-surcharge-percent-input"
                      />
                      <span className="text-sm text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Example: On a $100 payment, the fee will be {cs()}{(100 * (parseFloat(ccSurchargePercent) || 0) / 100).toFixed(2)}
                    </p>
                  </div>
                )}

                <Button
                  onClick={saveSurchargeSettings}
                  disabled={savingSurcharge}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-surcharge-btn"
                >
                  {savingSurcharge ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Fee Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Webhook Activity Tab */}
          <TabsContent value="saved-pms" className="mt-6">
            <PaymentMethodsDirectory />
          </TabsContent>

          <TabsContent value="webhooks" className="mt-6 space-y-6">
            {/* Payment Sync Status */}
            {RETIRED_TOOLS_VISIBLE && (<Card className="border border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Payment Sync Status
                    </CardTitle>
                    <CardDescription>Overview of payment statuses across all collections</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchWebhookActivity}
                    disabled={loadingWebhooks}
                    data-testid="refresh-sync-status-btn"
                  >
                    {loadingWebhooks ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {paymentSyncStatus ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Enrollments */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium text-slate-700 mb-3">Enrollments</h4>
                      <div className="space-y-2">
                        {Object.entries(paymentSyncStatus.enrollment_payment_status || {}).map(([status, count]) => (
                          <div key={status} className="flex justify-between items-center">
                            <Badge className={`text-xs ${
                              status === 'paid' ? 'bg-green-100 text-green-700' :
                              status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {status || 'null'}
                            </Badge>
                            <span className="font-medium text-slate-900">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Lane Bookings */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium text-slate-700 mb-3">Lane Bookings</h4>
                      <div className="space-y-2">
                        {Object.entries(paymentSyncStatus.lane_booking_payment_status || {}).map(([status, count]) => (
                          <div key={status} className="flex justify-between items-center">
                            <Badge className={`text-xs ${
                              status === 'paid' ? 'bg-green-100 text-green-700' :
                              status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {status || 'null'}
                            </Badge>
                            <span className="font-medium text-slate-900">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Tournament Registrations */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium text-slate-700 mb-3">Tournaments</h4>
                      <div className="space-y-2">
                        {Object.entries(paymentSyncStatus.tournament_payment_status || {}).map(([status, count]) => (
                          <div key={status} className="flex justify-between items-center">
                            <Badge className={`text-xs ${
                              status === 'paid' ? 'bg-green-100 text-green-700' :
                              status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {status || 'null'}
                            </Badge>
                            <span className="font-medium text-slate-900">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    {loadingWebhooks ? 'Loading...' : 'No data available'}
                  </div>
                )}
              </CardContent>
            </Card>)}
            
            {/* Webhook Logs */}
            <Card className="border border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Webhook className="h-5 w-5" />
                      Webhook Event Logs
                    </CardTitle>
                    <CardDescription>Real-time Stripe webhook events received by your application</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedEventType}
                      onChange={(e) => setSelectedEventType(e.target.value)}
                      className="px-3 py-2 text-sm border border-slate-200 rounded-md"
                      data-testid="event-type-filter"
                    >
                      <option value="all">All Events</option>
                      {eventTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refreshWebhookLogs}
                      disabled={loadingWebhooks}
                      data-testid="refresh-logs-btn"
                    >
                      {loadingWebhooks ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {webhookLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Time</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Event Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stripe ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Metadata</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {webhookLogs.filter(log => selectedEventType === 'all' || log.event_type === selectedEventType).map((log) => (
                          <tr key={log.id} className="hover:bg-slate-50" data-testid={`webhook-log-${log.id}`}>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              <div className="flex items-center gap-2">
                                <Clock className="h-3 w-3 text-slate-400" />
                                {new Date(log.created_at).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`text-xs ${
                                log.event_type?.includes('completed') || log.event_type?.includes('succeeded') 
                                  ? 'bg-green-100 text-green-700' :
                                log.event_type?.includes('failed') || log.event_type?.includes('expired')
                                  ? 'bg-red-100 text-red-700' :
                                log.event_type?.includes('refunded')
                                  ? 'bg-purple-100 text-purple-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {log.event_type}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                              {log.stripe_id?.substring(0, 20)}...
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {log.metadata && Object.keys(log.metadata).length > 0 ? (
                                <span className="font-mono text-xs">
                                  {Object.entries(log.metadata).map(([k, v]) => `${k}: ${v}`).join(', ').substring(0, 50)}
                                  {Object.entries(log.metadata).map(([k, v]) => `${k}: ${v}`).join(', ').length > 50 ? '...' : ''}
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {log.processed ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Processed
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Webhook className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">No webhook events yet</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Webhook events will appear here when Stripe sends notifications about payment activities.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Webhook Setup Info */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Webhook Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    Your Webhook URLs{" "}
                    <span className="text-xs text-emerald-700 font-normal">
                      (tenant-scoped — CR-02)
                    </span>:
                  </p>
                  {webhookUrlsLoading ? (
                    <p className="text-xs text-slate-500">Loading…</p>
                  ) : (
                    <div className="space-y-2" data-testid="webhook-url-panel">
                      {['test', 'live'].map((m) => (
                        <div key={m} className="flex items-center gap-2">
                          <span className="text-[10px] uppercase font-semibold text-slate-500 w-12">{m}</span>
                          <code
                            className="flex-1 text-sm bg-white px-3 py-2 rounded border border-blue-200 font-mono break-all"
                            data-testid={`webhook-url-${m}`}
                          >
                            {webhookUrls[m] || `Not provisioned`}
                          </code>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!webhookUrls[m]}
                            onClick={() => {
                              navigator.clipboard.writeText(webhookUrls[m]);
                              toast.success(`${m} webhook URL copied`);
                            }}
                            data-testid={`webhook-url-copy-${m}`}
                          >
                            Copy
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => rotateTenantWebhookUrl(m)}
                            data-testid={`webhook-url-rotate-${m}`}
                          >
                            Rotate
                          </Button>
                        </div>
                      ))}
                      <p className="text-xs text-amber-700 mt-1">
                        Rotate immediately if you suspect a key leak. Update Stripe
                        Dashboard → Developers → Webhooks with the new URL after rotating.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Events being monitored:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-green-100 text-green-700">checkout.session.completed</Badge>
                    <Badge className="bg-red-100 text-red-700">checkout.session.expired</Badge>
                    <Badge className="bg-green-100 text-green-700">payment_intent.succeeded</Badge>
                    <Badge className="bg-red-100 text-red-700">payment_intent.payment_failed</Badge>
                    <Badge className="bg-purple-100 text-purple-700">charge.refunded</Badge>
                  </div>
                </div>
                
                <Alert className="border-amber-300 bg-amber-50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-800">Important</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    To enable webhook signature verification, add your webhook signing secret in the Settings tab.
                    This ensures that webhook events are genuinely from Stripe.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Invoice Reminders Tab */}
          <TabsContent value="reminders" className="mt-6 space-y-6">
            {/* Overdue Summary Card */}
            <Card className="border border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Pending Overdue Invoices
                    </CardTitle>
                    <CardDescription>
                      Invoices requiring payment attention
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchReminderStats}
                    disabled={loadingReminders}
                    data-testid="refresh-reminder-stats-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingReminders ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingReminders ? (
                  <div className="flex items-center justify-center h-32">
                    <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : reminderStats?.pending_overdue ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-600 mb-1">Program Enrollments</p>
                      <p className="text-2xl font-bold text-amber-700">{reminderStats.pending_overdue.enrollments}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-600 mb-1">Lane Bookings</p>
                      <p className="text-2xl font-bold text-blue-700">{reminderStats.pending_overdue.lane_bookings}</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-600 mb-1">Tournaments</p>
                      <p className="text-2xl font-bold text-purple-700">{reminderStats.pending_overdue.tournaments}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-600 mb-1">Total Outstanding</p>
                      <p className="text-2xl font-bold text-red-700">{reminderStats.pending_overdue.total}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No overdue data available</p>
                )}
              </CardContent>
            </Card>
            
            {/* Manual Reminder Actions */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Send Reminders Manually
                </CardTitle>
                <CardDescription>
                  Trigger reminder emails to parents with overdue invoices. Reminders are rate-limited to avoid spam (1 email per 3 days per parent).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-4">
                  <Button 
                    onClick={runInvoiceReminders}
                    disabled={runningReminders}
                    data-testid="run-invoice-reminders-btn"
                  >
                    {runningReminders ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Send Reminder Emails Now
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={resetReminderTracking}
                    data-testid="reset-reminder-tracking-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Reset Rate Limits
                  </Button>
                </div>
                
                <Alert className="border-blue-300 bg-blue-50">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-800">Automatic Scheduling</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    Invoice reminders are automatically sent daily at 9:00 AM UTC. The email includes program/event names, due dates, and amounts owed.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
            
            {/* Recent Reminders Card */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Recent Reminders Sent
                </CardTitle>
                <CardDescription>
                  {reminderStats?.total_reminders_sent || 0} total reminders sent
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reminderStats?.recent_reminders?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Parent Email</th>
                          <th className="text-center py-2 px-3">Items</th>
                          <th className="text-right py-2 px-3">Amount</th>
                          <th className="text-center py-2 px-3">Sent Count</th>
                          <th className="text-right py-2 px-3">Last Sent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reminderStats.recent_reminders.map((reminder, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-3 text-slate-700">{reminder.parent_email}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge variant="outline">{reminder.items_count} items</Badge>
                            </td>
                            <td className="py-2 px-3 text-right font-medium text-red-600">
                              {cs()}{(reminder.total_amount || 0).toFixed(2)}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <Badge className="bg-slate-100 text-slate-600">{reminder.reminder_count || 1}</Badge>
                            </td>
                            <td className="py-2 px-3 text-right text-slate-500">
                              {reminder.sent_at ? new Date(reminder.sent_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No reminders sent yet</p>
                    <p className="text-sm mt-1">Use the button above to send reminders to parents with overdue invoices</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="drift" className="mt-6">
            <TenantDriftPanel endpoint="/admin/payment-settings/drift-findings?limit=50" />
          </TabsContent>
        </Tabs>

        {/* Mode Switch Confirmation Dialog */}
        <Dialog open={showModeDialog} onOpenChange={setShowModeDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className={`font-heading text-xl flex items-center gap-2 ${pendingMode === 'live' ? 'text-red-600' : 'text-blue-600'}`}>
                {pendingMode === 'live' ? (
                  <><AlertTriangle className="h-5 w-5" /> Switch to Live Mode?</>
                ) : (
                  <><TestTube className="h-5 w-5" /> Switch to Test Mode?</>
                )}
              </DialogTitle>
              <DialogDescription>
                {pendingMode === 'live' 
                  ? 'This will enable real payment processing.'
                  : 'This will disable real payment processing.'
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {pendingMode === 'live' ? (
                <Alert className="border-red-300 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertTitle className="text-red-800">Warning!</AlertTitle>
                  <AlertDescription className="text-red-700 space-y-2">
                    <p>By switching to live mode:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>All payments will charge <strong>real money</strong></li>
                      <li>Customers&apos; credit cards will be billed</li>
                      <li>Refunds will require manual processing</li>
                      <li>Transaction fees will apply</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-blue-300 bg-blue-50">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-700">
                    Switching to test mode will use test API keys. No real payments will be processed.
                    Existing live payments and subscriptions are not affected.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="mt-4 flex items-start gap-2">
                <input
                  type="checkbox"
                  id="confirm-mode-switch"
                  checked={confirmModeSwitch}
                  onChange={(e) => setConfirmModeSwitch(e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="confirm-mode-switch" className="text-sm text-slate-600">
                  {pendingMode === 'live'
                    ? 'I understand that real payments will be processed and customers will be charged actual money.'
                    : 'I understand that switching to test mode will not process real payments.'
                  }
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowModeDialog(false);
                setConfirmModeSwitch(false);
              }}>
                Cancel
              </Button>
              <Button
                onClick={handleModeSwitch}
                disabled={!confirmModeSwitch || saving}
                className={pendingMode === 'live' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Switching...</>
                ) : (
                  <>Switch to {pendingMode === 'live' ? 'Live' : 'Test'} Mode</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Live Keys Confirmation Dialog */}
        <Dialog open={showLiveKeysDialog} onOpenChange={setShowLiveKeysDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2 text-red-600">
                <Shield className="h-5 w-5" /> Confirm Live Keys Update
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <Alert className="border-amber-300 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Security Notice</AlertTitle>
                <AlertDescription className="text-amber-700 space-y-2">
                  <p>You are about to save <strong>live production keys</strong> that will:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Enable real payment processing when live mode is active</li>
                    <li>Process actual credit card charges</li>
                    <li>Be stored securely in your database</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <div className="mt-4 flex items-start gap-2">
                <input
                  type="checkbox"
                  id="confirm-live-keys"
                  checked={confirmLiveKeys}
                  onChange={(e) => setConfirmLiveKeys(e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="confirm-live-keys" className="text-sm text-slate-600">
                  I confirm these are my legitimate Stripe live API keys and I understand they will process real payments when live mode is enabled.
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowLiveKeysDialog(false);
                setConfirmLiveKeys(false);
              }}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveLiveKeys}
                disabled={!confirmLiveKeys || saving}
                className="bg-red-600 hover:bg-red-700"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Confirm & Save</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StripeSettings;
